import { Injectable } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, map } from 'rxjs';

import { NumpadDisplayService } from '../../../../../lib/numpad/numpad-display.service';
import { IBetslipRootState } from '../../../../base/store/state';
import { BetslipType } from '../../../../core/betslip-type';
import { PickId } from '../../../../core/picks/pick-id';
import { getSingleSlipId } from '../../../bet-generation/services/slip.utils';
import { FreeBetToken } from '../../../reward-tokens/reward-tokens.model';
import { selectFreeBetTokenFactory } from '../../../reward-tokens/selectors';
import { BetslipStakeActions } from '../../../stake/actions';
import { BETSLIP_LINEAR_NUMPAD, IStake } from '../../../stake/models';
import { ILinearContainerStakeService } from '../../../stake/services/digital/linear/linear-container-stake.interface';
import { IStakeErrorViewModel } from '../../../summary/models/stake-errors.model';
import { LinearStakeErrorsService } from '../../../summary/services/linear/linear-stake-errors.service';
import { selectSinglePicksState } from '../../../types/selectors';
import BetslipSingleBetActions from '../../actions';
import { singleBetPicksSelector } from '../../selectors';

@Injectable()
export class SingleBetContainerStakeService implements ILinearContainerStakeService {
    private stake$: Observable<IStake | null>;
    private stakeError$: Observable<IStakeErrorViewModel>;

    private pickId?: PickId;
    private adornmentText$: Observable<string | undefined>;
    private readonly hasGeneralSelectionStakeField: boolean;

    constructor(
        private store: Store<IBetslipRootState>,
        private stakeErrorService: LinearStakeErrorsService,
        private numpadDisplayService: NumpadDisplayService,
        betslipConfig: BetslipConfig,
    ) {
        this.hasGeneralSelectionStakeField = betslipConfig.selectionsGeneralStakeEnabled;
    }

    init(pickId: PickId) {
        this.pickId = pickId;
        this.adornmentText$ = this.store
            .select(selectSinglePicksState)
            .pipe(map((state) => (state.picks[pickId.toString()]?.isEachWay ? '2x' : undefined)));

        const picks$ = this.store.select(singleBetPicksSelector);

        this.stake$ = picks$.pipe(
            map((picks) => {
                const pick = picks[this.pickId!.toString()];

                if (!pick) {
                    return {
                        stake: null,
                        actualStake: null,
                    };
                }

                return {
                    actualStake: pick.actualStake,
                    stake: pick.stake,
                };
            }),
        );

        const errors$ = this.stakeErrorService.getStakeErrors$(getSingleSlipId(pickId));

        this.stakeError$ = combineLatest([errors$, picks$]).pipe(
            map(([errors, picks]) => {
                const pick = picks[this.pickId!.toString()];

                if (!pick) {
                    return {
                        hasStakeErrors: false,
                    };
                }

                const stake = { stake: pick.stake, actualStake: pick.actualStake };

                const pickValues = Object.values(picks);

                const allFieldsHaveEqualStake =
                    this.hasGeneralSelectionStakeField && pickValues.length > 1 && pickValues.every((p) => p.stake === stake.stake);

                const isNumpadOpen = this.numpadDisplayService.isOpen(this.getKeypadId());

                return this.stakeErrorService.convertToViewModel(errors, stake, isNumpadOpen, allFieldsHaveEqualStake);
            }),
        );
    }

    getAdornmentText(): Observable<string | undefined> {
        return this.adornmentText$;
    }

    getKeypadId(): string {
        return BETSLIP_LINEAR_NUMPAD;
    }

    setStake(stakeStr: string, stake: number | null): void {
        if (!this.pickId) {
            return;
        }

        this.store.dispatch(BetslipSingleBetActions.setSingleBetPickStake({ pickId: this.pickId.toString(), stake, stakeStr, silent: false }));
    }

    clickStake(stake: IStake | null): void {
        const stakeAmount = stake?.stake ?? null;

        this.store.dispatch(BetslipStakeActions.trackStakeClick({ stakeAmount, betslipType: BetslipType.Single }));
    }

    getStake$(): Observable<IStake | null> {
        return this.stake$;
    }

    getStakeErrors$(): Observable<IStakeErrorViewModel> {
        return this.stakeError$;
    }

    getFreeBetToken$(): Observable<FreeBetToken | null> {
        return this.store.select(selectFreeBetTokenFactory({ pickId: this.pickId, betslipType: BetslipType.Single }));
    }
}
